import { Injectable } from '@angular/core';
import { DevSettingsService, FeatureFlagsKeysEnum } from 'core/services/dev-settings.service';
import { BehaviorSubject } from 'rxjs';
import { isBrowserOnline } from '../../planograms-editor/store/autosave-network-status';
import { createEditorDraftIntervalScheduler, EditorDraftIntervalScheduler } from '../../planograms-editor/editor-draft-interval-scheduler';
import { AutosaveUiStatus, EditorAutosaveSessionConfig } from './spaces-editor-draft.models';

const SYNC_OK_DISPLAY_MS = 2500;
const DEFAULT_AUTOSAVE_INTERVAL_MS = 300_000;
//No autoguarda mientras el usuario sigue editando: exige una pausa de este largo desde el último
//cambio antes de disparar, además de que haya algo para guardar (isDirty).
const AUTOSAVE_STABLE_DEBOUNCE_MS = 10_000;
/**
 * Cuántos fallos de red seguidos se reintentan antes de darlos por un fallo real.
 *
 * "Sin conexión: se reintenta solo" es una promesa, y contra un servidor que no responde nunca
 * sería mentira: a los N intentos (N minutos) se reporta como el error que es y se corta la cadena.
 * El presupuesto no lo consume un corte de red genuino, porque mientras `navigator.onLine` está en
 * false no se intenta nada y al volver la conexión el contador se reinicia. Sólo lo consume el caso
 * "hay red pero el servidor no contesta", que es justamente el que no se arregla esperando.
 */
const MAX_CONSECUTIVE_OFFLINE_FAILURES = 5;

@Injectable({ providedIn: 'root' })
export class SpacesEditorDraftService {
  private session: EditorAutosaveSessionConfig | null = null;
  private intervalScheduler: EditorDraftIntervalScheduler | null = null;
  private autosaveInFlight = false;
  //CATMAN-S38: tras un autoguardado fallido no se vuelve a intentar hasta que un guardado manual funcione.
  private autosaveSuspended = false;
  //CATMAN-S38: momento en que terminó el último guardado (autosave o manual). El intervalo es fijo,
  //así que si el backend tarda cerca de un minuto el tick siguiente cae apenas termina el POST: sin este
  //cooldown el editor guardaría en ráfaga. Se exige un intervalo completo desde el FIN del último guardado.
  private lastSaveEndedAt: number | null = null;
  //Momento del último cambio notificado por notifyChange(). null = todavía no se observó ninguno
  //en la sesión actual (no bloquea: no hay ráfaga de la que esperar).
  private lastChangeAt: number | null = null;
  //Fallos de red seguidos. Lo reinicia cualquier señal de que el canal volvió: un guardado que
  //funciona, uno manual exitoso, o el evento `online` del browser.
  private consecutiveOfflineFailures = 0;
  private syncOkTimer: ReturnType<typeof setTimeout> | null = null;
  //Se guardan las referencias porque removeEventListener necesita la misma función que se registró.
  private connectivityListeners: { onOffline: () => void; onOnline: () => void } | null = null;

  private readonly syncStatusSubject = new BehaviorSubject<AutosaveUiStatus>('hidden');
  readonly syncStatus$ = this.syncStatusSubject.asObservable();

  constructor(private readonly configFlags: DevSettingsService) {}

  isAutosaveFeatureEnabled(): boolean {
    return this.configFlags.hasFeatureFlag(FeatureFlagsKeysEnum['FeatureFlag__CAT-S38-AUTOSAVE']);
  }

  startSession(config: EditorAutosaveSessionConfig): void {
    this.endSession();
    if (!this.isAutosaveFeatureEnabled() || !(config.entityId > 0)) return;

    this.session = config;
    this.autosaveSuspended = false;
    this.lastSaveEndedAt = null;
    this.lastChangeAt = null;
    this.setSyncStatus('hidden');
    this.startConnectivityListeners();
    this.startIntervalScheduler();
  }

  endSession(): void {
    this.intervalScheduler?.dispose();
    this.intervalScheduler = null;
    this.stopConnectivityListeners();
    this.clearSyncOkTimer();
    this.session = null;
    this.autosaveInFlight = false;
    this.autosaveSuspended = false;
    this.lastSaveEndedAt = null;
    this.lastChangeAt = null;
    this.consecutiveOfflineFailures = 0;
    this.setSyncStatus('hidden');
  }

  /**
   * Marca que el planograma en edición cambió recién. El editor la llama en cada emisión de
   * `planogramToEdit` (cada edición real). Alimenta el debounce de `canAutosaveNow`: no tiene sentido
   * disparar el POST a mitad de una ráfaga de cambios que todavía se están escribiendo.
   */
  notifyChange(): void {
    if (!this.session) return;
    this.lastChangeAt = Date.now();
  }

  setDragActive(active: boolean): void {
    if (!this.intervalScheduler) return;
    if (active) {
      this.intervalScheduler.pause();
    } else {
      this.intervalScheduler.resume();
    }
  }

  /**
   * Cierra el intento de autoguardado en curso. Sólo tiene efecto si hay uno en vuelo:
   * completions duplicadas o tardías (p. ej. una respuesta que llega después de endSession) se ignoran.
   */
  completeAutosave(success: boolean): void {
    if (!this.autosaveInFlight) return;
    this.autosaveInFlight = false;
    //Hubo POST: el próximo autoguardado espera un intervalo completo desde acá, no desde que arrancó.
    this.lastSaveEndedAt = Date.now();
    if (!this.session) return;

    if (!success) {
      //Se corta la cadena de autoguardados: sólo un guardado manual exitoso la reanuda.
      this.autosaveSuspended = true;
      this.setSyncStatus('sync_error');
      return;
    }

    //El canal volvió: el presupuesto de reintentos por red se renueva entero.
    this.consecutiveOfflineFailures = 0;
    this.setSyncStatus('synced');
    this.clearSyncOkTimer();
    this.syncOkTimer = setTimeout(() => this.setSyncStatus('hidden'), SYNC_OK_DISPLAY_MS);
  }

  /**
   * Cierra el intento en curso como "no hubo conexión". A diferencia de `completeAutosave(false)`,
   * NO suspende la cadena: un corte de red se arregla solo, así que el editor avisa y sigue
   * reintentando en cada tick hasta que vuelva. El cartel se limpia solo al volver la conexión.
   */
  failAutosaveOffline(): void {
    if (!this.autosaveInFlight) return;
    this.autosaveInFlight = false;
    this.lastSaveEndedAt = Date.now();
    if (!this.session) return;

    this.consecutiveOfflineFailures++;

    //Se agotó el presupuesto de reintentos: ya no es un corte pasajero, es un fallo.
    if (this.consecutiveOfflineFailures >= MAX_CONSECUTIVE_OFFLINE_FAILURES) {
      this.autosaveSuspended = true;
      this.setSyncStatus('sync_error');
      return;
    }

    this.setSyncStatus('offline');
  }

  /**
   * Cierra el intento en curso como "el editor no llegó a armar el guardado": una excepción en el
   * clonado, la comparación con el original o el precálculo de estantes, antes de cualquier POST.
   *
   * Suspende igual que `completeAutosave(false)` — reintentar no va a cambiar el resultado — pero se
   * distingue en pantalla a propósito: es el único fallo que no deja rastro en ningún lado (no hay
   * request, así que no hay Network ni log de backend), y hasta ahora se reportaba con el mismo
   * cartel que un backend caído. Que el cartel diga otra cosa es lo que permite clasificarlo cuando
   * lo único que llega es una captura de pantalla.
   */
  failAutosavePrepare(): void {
    if (!this.autosaveInFlight) return;
    this.autosaveInFlight = false;
    this.lastSaveEndedAt = Date.now();
    if (!this.session) return;

    this.autosaveSuspended = true;
    this.setSyncStatus('prepare_error');
  }

  /**
   * Aborta el intento en curso sin marcarlo como error: se usa cuando no había nada para guardar
   * (planograma sin id, sin cambios) o cuando otro guardado ocupaba el canal. Se reintenta en el próximo tick.
   */
  cancelAutosave(): void {
    if (!this.autosaveInFlight) return;
    this.autosaveInFlight = false;
    if (!this.session) return;
    if (this.syncStatusSubject.value === 'syncing') this.setSyncStatus('hidden');
  }

  /**
   * Un guardado manual exitoso limpia el error y reanuda el autoguardado.
   * No toca `autosaveInFlight` a propósito: ese flag es el que garantiza un único guardado en vuelo
   * y sólo lo libera el cierre del intento (completeAutosave/cancelAutosave/endSession).
   */
  notifyManualSaveSuccess(): void {
    this.autosaveSuspended = false;
    this.consecutiveOfflineFailures = 0;
    //El guardado manual también cuenta como "último guardado": no se autoguarda pisándole los talones.
    this.lastSaveEndedAt = Date.now();
    if (!this.session) return;
    this.clearSyncOkTimer();
    this.setSyncStatus('hidden');
  }

  isAutosaveSuspended(): boolean {
    return this.autosaveSuspended;
  }

  /** false cuando ya no hay editor abierto (se navegó o cambió de planograma): no hay a quién avisarle nada. */
  hasActiveSession(): boolean {
    return this.session != null;
  }

  /**
   * El browser avisa cuando se cae y cuando vuelve la conexión. Sirve para dos cosas que el ciclo
   * de autoguardado no puede hacer solo: avisar del corte apenas pasa (y no hasta un minuto después,
   * cuando falle el próximo intento) y limpiar el cartel al volver, sin esperar al tick siguiente.
   */
  private startConnectivityListeners(): void {
    if (typeof window === 'undefined' || this.connectivityListeners) return;

    const onOffline = () => {
      //Sólo se avisa si hay algo en riesgo: sin cambios pendientes, el corte no le importa a nadie.
      if (this.session?.isDirty?.()) this.setSyncStatus('offline');
    };

    const onOnline = () => {
      //Volvió la conexión: el próximo tick reintenta con el presupuesto entero. Un corte genuino no
      //tiene por qué gastar los intentos reservados para "hay red y el servidor no contesta".
      this.consecutiveOfflineFailures = 0;
      if (this.syncStatusSubject.value === 'offline') this.setSyncStatus('hidden');
    };

    window.addEventListener('offline', onOffline);
    window.addEventListener('online', onOnline);
    this.connectivityListeners = { onOffline, onOnline };
  }

  private stopConnectivityListeners(): void {
    if (typeof window === 'undefined' || !this.connectivityListeners) return;

    window.removeEventListener('offline', this.connectivityListeners.onOffline);
    window.removeEventListener('online', this.connectivityListeners.onOnline);
    this.connectivityListeners = null;
  }

  private startIntervalScheduler(): void {
    this.intervalScheduler?.dispose();
    this.intervalScheduler = createEditorDraftIntervalScheduler({
      intervalMs: DEFAULT_AUTOSAVE_INTERVAL_MS,
      canSync: () => this.canAutosaveNow(),
      onTick: () => this.triggerAutosave(),
    });
  }

  private canAutosaveNow(): boolean {
    const session = this.session;
    if (!session || !this.isAutosaveFeatureEnabled()) return false;
    if (this.autosaveInFlight) return false;
    if (this.autosaveSuspended) return false;
    //Sin conexión el POST no puede llegar: se ahorra el intento y se deja el cartel a la vista.
    if (!isBrowserOnline()) return false;
    if (!this.isCooldownElapsed()) return false;
    if (session.isSaving?.()) return false;
    if (session.isDragging?.()) return false;
    if (!session.isDirty?.()) return false;
    if (!this.isChangeStable()) return false;
    if (!(session.entityId > 0)) return false;
    return session.getPayload() != null;
  }

  /** true si pasó al menos un intervalo completo desde que terminó el último guardado. */
  private isCooldownElapsed(): boolean {
    if (this.lastSaveEndedAt == null) return true;
    return Date.now() - this.lastSaveEndedAt >= DEFAULT_AUTOSAVE_INTERVAL_MS;
  }

  /** true si no hay cambios recientes (o todavía no se observó ninguno) — no hay ráfaga en curso. */
  private isChangeStable(): boolean {
    if (this.lastChangeAt == null) return true;
    return Date.now() - this.lastChangeAt >= AUTOSAVE_STABLE_DEBOUNCE_MS;
  }

  private triggerAutosave(): void {
    if (!this.canAutosaveNow()) return;
    const session = this.session;
    if (!session) return;

    this.autosaveInFlight = true;
    this.setSyncStatus('syncing');

    try {
      session.onAutosave();
    } catch (e) {
      //Última red de contención: onAutosave ya atrapa sus propios errores, pero si algo escapa
      //el intento se cierra como fallido (y queda suspendido) en vez de dejar el flag en vuelo para siempre.
      console.error('[SpacesEditorDraft] onAutosave lanzó una excepción', e);
      this.failAutosavePrepare();
    }
  }

  private setSyncStatus(status: AutosaveUiStatus): void {
    if (this.syncStatusSubject.value !== status) {
      this.syncStatusSubject.next(status);
    }
  }

  private clearSyncOkTimer(): void {
    if (this.syncOkTimer) {
      clearTimeout(this.syncOkTimer);
      this.syncOkTimer = null;
    }
  }
}
